import * as React from "react";
import { IBasket } from "../models/IBasket.model";
import DeliveryBar from "./DeliveryBar";
import ConfigService from "../services/ConfigService";
import PaymentIcons from "./PaymentIcons";
import TrustPilot from "./TrustPilot";
import BasketContext from "../services/BasketContext";
import { IPrice } from "../models/IPrice.model";
import { IInstalments } from "../../../gulp/scripts/typescript/models/IInstalments.model";
import ExpressPayPalButtons from "./ExpressPayPalButtons";
import ExpressStripeWallets from "./ExpressStripeWallets";

declare var Tooltip: any;

const PAYPAL_FINANCE_DISPLAY_NAME = "PayPal";

type OrderSummaryExpressCheckoutProps = IBasket & { financeBanners: IInstalments[] };

// Express-checkout A/B test variant of OrderSummary.tsx - identical except it also renders
// the Express PayPal/Stripe wallet buttons. Compiled into its own bundle
// (basket-express-checkout.min.js, see gulpfile.mjs basketExpressCheckoutUI()).
function OrderSummaryExpressCheckout(props: OrderSummaryExpressCheckoutProps) {

	// Trade-customer/transaction-limit eligibility for PayPal's "Pay in 3" - PayPal's own SDK
	// eligibility check (which the paylater button also has) reflects PayPal's dynamic buyer/amount
	// rules only, not this site's trade-customer exclusion, so this must be checked here too.
	const payPalPayIn3Available = props.financeBanners?.find(
		(banner) => banner.DisplayName === PAYPAL_FINANCE_DISPLAY_NAME
	)?.IsAvailable ?? false;

	const configService = new ConfigService();
	const basketContext = React.useContext(BasketContext);
	const [isStripeWalletAvailable, setIsStripeWalletAvailable] = React.useState<boolean | null>(null);
	const isPayPalAvailable = !!configService.imageUrls.paypalIconUrl;
	const showExpressCheckout = isPayPalAvailable || isStripeWalletAvailable !== false;

	const subtotalText = props.IsTradeCustomer ? "Subtotal ex. VAT" : "Subtotal (including VAT)";
	const estimatedDeliveryText = props.IsTradeCustomer ? "Estimated delivery inc. VAT" : "Estimated delivery";
	const totalToPayText = props.IsTradeCustomer ? "Total to pay inc. VAT" : "Total to pay";

	React.useEffect(() => {

		if (typeof Tooltip != "undefined") {
			Tooltip.init(false);
		}

	}, []);

	function handleCheckoutButtonClick(e: React.MouseEvent) {
		if (isQuantityExceeded()) {
			e.preventDefault();
		}
	}

	function isQuantityExceeded() {
		let isExceeded = false;

		for (let index = 0; index < props.LineItems.length; index++) {
			const lineItem = props.LineItems[index];

			if (lineItem.Quantity > lineItem.QuantityMax) {

				const elementToFocus = document.querySelector(`[data-test-product-id="${lineItem.ProductName}"] [data-test="basket-item-quantity"]`) as HTMLInputElement;

				if (elementToFocus) {
					elementToFocus.focus();
					isExceeded = true;
					break;
				}
			}
		}
		return isExceeded;
	}

	function renderShippingPrice(shippingTotal: IPrice, shippingTotalBeforeDiscounts: IPrice) {

		if (shippingTotal.Price > 0) {
			if (shippingTotalBeforeDiscounts.Price > shippingTotal.Price) {
				return (
					<React.Fragment>
						<span className="c-basket__total-was-price">{shippingTotalBeforeDiscounts.Formatted}</span>
						<span className="c-basket__discount">{shippingTotal.Formatted}</span>
					</React.Fragment>
				);
			} else {
				return shippingTotal.Formatted;
			}
		} else {
			return 'Free';
		}
	}

	return (
		<div className="c-basket__sidebar-item">
			<h2 className="c-basket__sidebar-title">Order Summary</h2>

			<table className="c-basket__total">
				<tbody>
					{props.LineItems.map((lineItem) => {
						return lineItem.Discounts.map((discount, index) => {
							return <tr key={discount.DiscountId}>
								<th className="c-basket__discount " scope="row" data-test="basket-summary-line-item-discount">
									Discount code: {discount.Description}
								</th>
								<td className="c-basket__discount c-basket__price-cell" data-test="basket-summary-line-item-discount-amount">{discount.Amount.Formatted}</td>
							</tr>;
						});
					})}

					{props.Discounts.map((discount, index) => {
						return <tr key={discount.DiscountId}>
							<th className="c-basket__discount " scope="row" data-test="basket-discount">
								Discount code: {discount.Description}
							</th>
							<td className="c-basket__discount c-basket__price-cell" data-test="basket-discount-amount">{discount.Amount.Formatted}</td>
						</tr>;
					})}

					<tr>
						<th scope="row" data-test="basket-subtotal-text">{subtotalText}</th>
						<td data-test="basket-subtotal" className="c-basket__price-cell">
							{props.IsTradeCustomer ? props.SubTotalExVat.Formatted
								: props.SubTotal.Formatted}
						</td>
					</tr>

					{props.IsTradeCustomer ?
						<tr>
							<th scope="row" data-test="basket-trade-vat-text">
								VAT
							</th>
							<td data-test="basket-trade-vat" className="c-basket__price-cell">
								{props.TaxTotal.Formatted}
							</td>
						</tr>
						: null}

					<tr>
						<th scope="row" data-test="basket-shipping-total-text">
							{estimatedDeliveryText}
							<span className="js-tooltip-trigger-wrapper js-tooltip-trigger-wrapper--right">
								<a
									className="js-tooltip-trigger"
									href="#deliveryTooltip"
									data-js-tooltip-trigger
									data-js-tooltip-trigger-position="bottom"
									data-js-tooltip-append-target=".c-product__delivery__estimate .js-tooltip-trigger-wrapper"
									data-js-tooltip-hide-dimmer="true"
									data-test="tooltip-trigger"
								>
									<tooltip-trigger-icon />
									<span className="u-visuallyhidden">Help?</span>
								</a>
							</span>
							<div className="js-tooltip" id="deliveryTooltip" data-js-tooltip>
								<div
									className="js-tooltip__inner js-tooltip__inner--delivery js-tooltip__inner--font-normal"
									data-js-tooltip-inner
								>
									<div className="cms">
										<p>
											The delivery price depends on your location and delivery method of choice. This is the price of standard delivery to most of the UK.
										</p>
									</div>
								</div>
							</div>
						</th>
						<td data-test="basket-shipping-total" className="c-basket__price-cell">
							{ renderShippingPrice(props.ShippingTotal, props.ShippingTotalBeforeDiscounts) }
						</td>
					</tr>

					<tr>
						<th scope="row" className="c-basket__order-total">
							<strong data-test="basket-total-text">{totalToPayText}</strong>
						</th>
						<td className="c-basket__price-cell c-basket__order-total">
							<strong data-test="basket-total">{props.Total.Formatted}</strong>
						</td>
					</tr>

					{props.HasSavedTotal ?
						<tr>
							<th scope="row" className="c-basket__discount">
								You've saved
							</th>
							<td className="c-basket__discount c-basket__price-cell">
								<span data-test="basket-saved">{props.SavedTotal.Formatted}</span>
							</td>
						</tr>
						: null}

				</tbody>
			</table>

			<DeliveryBar {...props} />

			<a
				href="/checkout"
				data-no-instant
				data-test="go-to-checkout"
				className={`c-button c-button--large c-button--large c-button--emphasize c-button--full`}
				onClick={(e) => handleCheckoutButtonClick(e)}
			>
				<svg
					viewBox="0 0 24 24"
					className="c-basket__icon c-basket__icon--large"
				>
					<path
						fill="currentColor"
						d="M15.094 8.016v-2.016c0-1.688-1.406-3.094-3.094-3.094s-3.094 1.406-3.094 3.094v2.016h6.188zM12 17.016c1.078 0 2.016-0.938 2.016-2.016s-0.938-2.016-2.016-2.016-2.016 0.938-2.016 2.016 0.938 2.016 2.016 2.016zM18 8.016c1.078 0 2.016 0.891 2.016 1.969v10.031c0 1.078-0.938 1.969-2.016 1.969h-12c-1.078 0-2.016-0.891-2.016-1.969v-10.031c0-1.078 0.938-1.969 2.016-1.969h0.984v-2.016c0-2.766 2.25-5.016 5.016-5.016s5.016 2.25 5.016 5.016v2.016h0.984z"
					></path>
				</svg>
				Go to Checkout
			</a>

			{/* Stripe wallets have no GC on/off toggle (Stripe's own dashboard controls that). PayPal
				additionally reuses the existing GC PayPal toggle (paypalIconUrl). */}
			{!basketContext?.basket?.CanSplitDeliveries && showExpressCheckout ?
				<div className="c-basket__express-checkout" data-test="express-checkout">
					<span className="c-basket__express-checkout-label">Express Checkout</span>
					<ExpressStripeWallets onAvailabilityChange={setIsStripeWalletAvailable} />
					{configService.imageUrls.paypalIconUrl ?
						<React.Fragment>
							<ExpressPayPalButtons fundingSource="paypal" />
							{payPalPayIn3Available ?
								<ExpressPayPalButtons fundingSource="paylater" />
							: null}
						</React.Fragment>
					: null}
				</div>
			: null}

			<PaymentIcons position="basket-summary" />

			{configService.imageUrls.klarnaIconUrl && configService.imageUrls.clearpayIconUrl ?
				<TrustPilot {...basketContext.basket.BasketTrustpilotModel} />
			: null}

		</div>
	);
}

export default OrderSummaryExpressCheckout;
